/**
 * Shared markdown renderer for intelligence content (activity summaries,
 * scope summaries, agent-generated narratives). Uses CSS variables for
 * dark mode support.
 */
import ReactMarkdown from "react-markdown";

interface IntelligenceMarkdownProps {
  content: string;
  /** Compact mode uses smaller text and tighter spacing (for cards) */
  compact?: boolean;
  /** Maximum lines before truncation (uses CSS line-clamp) */
  lineClamp?: number;
  className?: string;
}

export default function IntelligenceMarkdown({
  content,
  compact = false,
  lineClamp,
  className = "",
}: IntelligenceMarkdownProps) {
  const textSize = compact ? "text-xs" : "text-sm";
  const spacing = compact ? "space-y-1.5" : "space-y-2.5";

  if (lineClamp) {
    return (
      <div className={`${className}`} style={{ display: "-webkit-box", WebkitLineClamp: lineClamp, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        <ReactMarkdown
          components={{
            p: ({ children }) => (
              <span className={`${textSize} leading-relaxed`} style={{ color: "var(--text-primary)" }}>{children} </span>
            ),
            strong: ({ children }) => (
              <strong className="font-semibold" style={{ color: "var(--text-primary)" }}>{children}</strong>
            ),
            em: ({ children }) => (
              <em className="italic" style={{ color: "var(--text-secondary)" }}>{children}</em>
            ),
            ul: ({ children }) => <span>{children}</span>,
            ol: ({ children }) => <span>{children}</span>,
            li: ({ children }) => <span>{children}; </span>,
            h1: () => null,
            h2: () => null,
            h3: () => null,
            h4: () => null,
            h5: () => null,
            h6: () => null,
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  }

  return (
    <div className={`${spacing} ${className}`}>
      <ReactMarkdown
        components={{
          p: ({ children }) => (
            <p className={`${textSize} leading-relaxed`} style={{ color: "var(--text-primary)" }}>{children}</p>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold" style={{ color: "var(--text-primary)" }}>{children}</strong>
          ),
          em: ({ children }) => (
            <em className="italic" style={{ color: "var(--text-secondary)" }}>{children}</em>
          ),
          ul: ({ children }) => (
            <ul className={`list-disc ml-5 ${spacing} ${textSize}`} style={{ color: "var(--text-primary)" }}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className={`list-decimal ml-5 ${spacing} ${textSize}`} style={{ color: "var(--text-primary)" }}>{children}</ol>
          ),
          li: ({ children }) => (
            <li className="leading-relaxed">{children}</li>
          ),
          h1: ({ children }) => (
            <h3 className="text-base font-semibold mt-4 first:mt-0" style={{ color: "var(--text-primary)" }}>{children}</h3>
          ),
          h2: ({ children }) => (
            <h3 className="text-base font-semibold mt-4 first:mt-0" style={{ color: "var(--text-primary)" }}>{children}</h3>
          ),
          h3: ({ children }) => (
            <h4 className="text-sm font-semibold mt-3 first:mt-0" style={{ color: "var(--text-primary)" }}>{children}</h4>
          ),
          h4: ({ children }) => (
            <h4 className="text-sm font-semibold mt-2 first:mt-0" style={{ color: "var(--text-primary)" }}>{children}</h4>
          ),
          h5: ({ children }) => (
            <h5 className="text-sm font-medium mt-2 first:mt-0" style={{ color: "var(--text-primary)" }}>{children}</h5>
          ),
          h6: ({ children }) => (
            <h6 className="text-xs font-medium mt-2 first:mt-0 uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>{children}</h6>
          ),
          hr: () => <hr className="my-4" style={{ borderColor: "var(--border)" }} />,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 pl-4 italic" style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>{children}</blockquote>
          ),
          code: ({ children }) => (
            <code className="text-xs px-1.5 py-0.5 rounded font-mono" style={{ color: "var(--text-primary)", backgroundColor: "var(--secondary)" }}>{children}</code>
          ),
          a: ({ href, children }) => {
            const safeHref = href && /^https?:\/\//i.test(href) ? href : undefined;
            if (!safeHref) return <span className="text-primary">{children}</span>;
            return (
              <a href={safeHref} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{children}</a>
            );
          },
          img: () => null,
          table: ({ children }) => (
            <div className="overflow-x-auto my-2">
              <table className="min-w-full text-sm border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="border-b" style={{ borderColor: "var(--border)" }}>{children}</thead>
          ),
          th: ({ children }) => (
            <th className="text-left py-2 pr-4 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-primary)" }}>{children}</th>
          ),
          td: ({ children }) => (
            <td className="py-1.5 pr-4 text-sm border-b" style={{ color: "var(--text-primary)", borderColor: "var(--border)" }}>{children}</td>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
